import { Injectable, inject } from '@angular/core';
import {StorageService} from "./storage.service";
import {StatesService} from "./states.service";
import {KeycloakTokenParsed} from "keycloak-js";
import Keycloak from 'keycloak-js';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  /**
   * Token as issued by the keycloak server.
   * @private
   */
  private token: string | undefined;

  /**
   * The parsed id token as a JavaScript object.
   * @private
   */
  private tokenParsed: KeycloakTokenParsed | undefined;

  private readonly keycloak = inject(Keycloak);

  constructor(
    private statesService: StatesService,
    private storageService: StorageService
  ) { }

  /**
   * Retrieves the parsed user Token and user States. Also creates an interval for automated periodic token renewal.
   */
  async processUserToken() {
    this.tokenParsed = this.keycloak.idTokenParsed;

    if (this.tokenParsed) {
      await this.statesService.setStates(this.tokenParsed);
    }

    setInterval(async () => {
      try {
        const refreshed = await this.keycloak.updateToken();
        if (refreshed) {
          console.log('Token refreshed');
        } else {
          console.log('Token not refreshed');
        }
      } catch (e) {
        console.error('Failed to refresh token', e);
      }
    }, 60000);
  }

  /**
   * Logs out the current user.
   */
  async logout() {
    await this.storageService.remove('username');
    await this.storageService.remove('active-state-id');
    await this.keycloak.logout();
  }

  /**
   * Retrieves the current user's username.
   * @return The current user's username
   */
  getUsername(): string | undefined {
    return this.tokenParsed?.['preferred_username'];
  }

  /**
   * Checks if the current user has a certain role
   * @param role - The role to check
   * @return Whether the user hast the given role
   */
  hasRole(role: string): boolean {
    return !!this.tokenParsed && this.tokenParsed['realm_access']?.roles.indexOf(role) !== -1;
  }

  /**
   * Checks if the current user has a role that includes the given substring.
   * @param substring - The substring to check
   * @return Whether the user has a role that includes the given substring
   */
  hasRoleSubstring(substring: string): boolean {
    let role = undefined;
    if (!!this.tokenParsed) {
      role = this.tokenParsed['realm_access']?.roles.find(str => str.includes(substring)); 
    }
    return !!role;
  }

  /**
   * Retrieves the roles of the current user.
   * @return The roles of the current user
   */
  getUserRoles(): string[] {
    return this.tokenParsed?.['realm_access']?.roles ? this.tokenParsed['realm_access']['roles'] : [];
  }
}
